/**
 * Link operations — the relationships between entries.
 */

import { newId } from '../ids'
import type { AppDocument } from '../schema'

/** Link an entry to a person. Returns the new link's id. */
export function linkEntryToPerson(
  doc: AppDocument,
  entryId: string,
  personId: string,
): string {
  const id = newId()
  doc.links[id] = {
    id,
    sourceId: entryId,
    targetId: personId,
    createdAt: new Date().toISOString(),
  }
  return id
}

/** Remove the link between an entry and a person, in whichever direction it was made. */
export function unlinkEntryFromPerson(
  doc: AppDocument,
  entryId: string,
  personId: string,
): void {
  const link = Object.values(doc.links).find(
    (candidate) =>
      (candidate.sourceId === entryId && candidate.targetId === personId) ||
      (candidate.sourceId === personId && candidate.targetId === entryId),
  )

  if (link) delete doc.links[link.id]
}

/**
 * Make an entry's people exactly `personIds`, adding and removing only what differs.
 *
 * **The diff is the point.** The hooks this replaces deleted every person link on the entry and
 * re-minted one per selected person with a fresh id. Because the new ids differ per device, the map
 * merge rule keeps both sets: two devices re-saving the *same unchanged* selection produce a
 * duplicate link, and one device clearing the selection is undone by the other's unrelated save
 * (#367 F6). Touching only what changed produces neither.
 *
 * Only links whose target is a person entry are considered, which is how the old code identified
 * them and is what keeps topic and project links out of it.
 */
export function setPersonLinks(
  doc: AppDocument,
  entryId: string,
  personIds: readonly string[],
): void {
  const wanted = new Set(personIds)

  for (const link of Object.values(doc.links)) {
    if (link.sourceId !== entryId) continue
    if (doc.entries[link.targetId]?.type !== 'person') continue

    if (wanted.has(link.targetId)) {
      // Already linked — leave the existing link alone rather than re-minting it
      wanted.delete(link.targetId)
    } else {
      delete doc.links[link.id]
    }
  }

  for (const personId of wanted) {
    linkEntryToPerson(doc, entryId, personId)
  }
}
